// Edit Product side panel for System Configuration > Product & Feature
// Configuration. Split out of the previously-combined SupportAdmin/index.js
// (2026-09-09) into its own file, alongside AddProduct.js and shared.js (the
// pieces identical between the two panels).
//
// Unlike Add Product, the product being configured here is already fixed --
// it's whichever row in the list was clicked -- so this panel does not show
// a Select Product dropdown. That's a deliberate difference from Add, not
// an oversight: letting someone swap the product mid-edit would just be a
// confusing way to do an Add.
import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { GlobalFieldLabel } from "components/DesignSystem/sharedUiPatterns";
import { customAPI } from "services/utils/CustomAPI";
import { notifyAccessChanged } from "services/utils/companyAccess";
import { useFetch } from "services/hooks/useFetch";
import { showToast } from "services/utils/Status";
import { designColors, PANEL_DRAWER_WIDTH } from "services/constants/designTokens";
import {
  ACCESS_DATE_MAX,
  ACCESS_DATE_MIN,
  ACCESS_FIELD_DEFAULTS,
  ACCESS_TYPE_API_VALUES,
  ACCESS_TYPE_LABELS,
  AccessValiditySection,
  ProductFeatureStatCards,
  ProductFeaturesSection,
  SaveConfigurationFooter,
  addon,
  isEffectivelyActive,
  snapshot,
} from "./shared";

// `product` is the already-mapped row from the List view's own `products`
// state (id = product_id, access_record_id = the access row's own primary
// key, name/description/access_type/dates/auto_expire/is_active already
// resolved -- see mapCompanyProduct in shared.js). `catalogEntry` is that
// same product's raw product-catalog/ entry (basic_features/addon_features
// arrays), passed down from the List view's already-loaded catalog so this
// panel doesn't need to fetch the whole catalog again just for one product.
export const EditProduct = ({
  open,
  product,
  catalogEntry,
  onClose,
  onRefresh,
  // Without the edit permission the panel opens as a locked, read-only view.
  canEdit = true,
}) => {
  // Product/feature configuration is schema-wide, not per-company, so no
  // company_id is read from the topbar or sent to the backend.
  const { domain } = useSelector((state) => state.tokenReducer);

  const [saving, setSaving] = useState(false);
  const [dateError, setDateError] = useState({});
  const [featureSearch, setFeatureSearch] = useState("");

  useEffect(() => {
    if (open) {
      setFeatureSearch("");
      setDateError({});
    }
  }, [open, product?.id]);

  // Fired the moment the panel opens -- returns the full, authoritative
  // detail for this exact company+product access record (confirmed
  // 2026-09-09): a single object (not a list), keyed by
  // company_product_id (= this row's own access_record_id, not product_id).
  // Its basic_features/addon_features carry this company's real per-feature
  // is_active overrides -- unlike product-feature-config/products/ (the
  // previous source here) or the product catalog, both of which only ever
  // reflect the feature's generic/default state, not what this specific
  // company has actually enabled or disabled. Using either of those as more
  // than a brief instant-loading fallback would silently show the wrong
  // toggle states whenever a company's configuration differs from default.
  const { result: accessDetailResult } = useFetch(
    open && product?.access_record_id
      ? `${domain}product/access/company-features/?company_product_id=${product.access_record_id}`
      : "",
  );
  const accessDetail =
    accessDetailResult && !Array.isArray(accessDetailResult)
      ? accessDetailResult
      : null;

  const [productFeatures, setProductFeatures] = useState([]);
  const [initialProductFeatures, setInitialProductFeatures] = useState("[]");
  useEffect(() => {
    if (!open || !product?.id) {
      setProductFeatures([]);
      setInitialProductFeatures("[]");
      return;
    }
    // Prefer the freshly-fetched, company-specific detail; fall back to the
    // already-loaded catalog entry only so the panel isn't empty while that
    // request is still in flight (its is_active values are generic, and get
    // replaced the moment accessDetail resolves).
    const detail = accessDetail || catalogEntry;
    const basicList = Array.isArray(detail?.basic_features)
      ? detail.basic_features
      : [];
    const addonList = Array.isArray(detail?.addon_features)
      ? detail.addon_features
      : [];
    const next = [...basicList, ...addonList];
    setProductFeatures(next);
    setInitialProductFeatures(snapshot(next));
  }, [open, product?.id, accessDetail, catalogEntry]);

  const [selectedAccess, setSelectedAccess] = useState(null);
  const [initialSelectedAccess, setInitialSelectedAccess] = useState("null");
  useEffect(() => {
    if (!open || !product) {
      setSelectedAccess(null);
      setInitialSelectedAccess("null");
      return;
    }
    // Prefer accessDetail (this exact record, fetched fresh the moment the
    // panel opened) over `product` (a snapshot from the list's last GET,
    // which can be out of date) -- same fallback-while-loading reasoning as
    // productFeatures above. access_type is re-normalized through
    // ACCESS_TYPE_LABELS either way: accessDetail's is the raw lowercase
    // backend enum ("licensed"), while product's was already normalized by
    // mapCompanyProduct ("Licensed") -- round-tripping the latter back
    // through the same lowercase-keyed lookup is a no-op, so one line
    // handles both sources correctly instead of silently keeping a
    // lowercase value that would fail every ACCESS_TYPES/ACCESS_TYPE_API_
    // VALUES comparison downstream (dropdown, "!== Internal" checks, save).
    const base = accessDetail || product;
    const next = {
      access_type:
        ACCESS_TYPE_LABELS[String(base.access_type || "").toLowerCase()] ||
        ACCESS_FIELD_DEFAULTS.access_type,
      start_date: base.start_date ?? ACCESS_FIELD_DEFAULTS.start_date,
      access_until:
        base.access_until ?? base.end_date ?? ACCESS_FIELD_DEFAULTS.access_until,
      auto_expire:
        base.auto_expire !== undefined
          ? base.auto_expire
          : ACCESS_FIELD_DEFAULTS.auto_expire,
    };
    setSelectedAccess(next);
    setInitialSelectedAccess(JSON.stringify(next));
  }, [open, product, accessDetail]);

  const selected = product && {
    ...product,
    ...(accessDetail && { name: accessDetail.product_name || product.name }),
    ...(selectedAccess || {}),
  };

  const productBasicFeatures = productFeatures.filter((item) => !addon(item));
  const productAddonFeatures = productFeatures.filter(addon);

  const updateProductField = (field, value) =>
    setSelectedAccess((prev) => (prev ? { ...prev, [field]: value } : prev));
  const updateProductFeature = (id, value) =>
    setProductFeatures((items) =>
      items.map((item) =>
        item.id === id ? { ...item, is_active: value } : item,
      ),
    );

  const close = () => {
    onClose();
  };

  const save = async () => {
    if (!canEdit) return undefined;
    if (!selected) return close();

    if (selected.access_type !== "Internal") {
      const errors = {};
      if (!selected.start_date) errors.start_date = "Start date is required.";
      if (!selected.access_until)
        errors.access_until = "Access until date is required.";
      if (
        selected.start_date &&
        (!dayjs(selected.start_date).isValid() ||
          dayjs(selected.start_date).isBefore(ACCESS_DATE_MIN, "day") ||
          dayjs(selected.start_date).isAfter(ACCESS_DATE_MAX, "day"))
      ) {
        errors.start_date = "Enter a valid start date.";
      }
      if (
        selected.access_until &&
        (!dayjs(selected.access_until).isValid() ||
          dayjs(selected.access_until).isBefore(ACCESS_DATE_MIN, "day") ||
          dayjs(selected.access_until).isAfter(ACCESS_DATE_MAX, "day"))
      ) {
        errors.access_until = "Enter a valid access until date.";
      }
      if (
        !errors.start_date &&
        !errors.access_until &&
        selected.start_date &&
        selected.access_until &&
        dayjs(selected.access_until).isBefore(dayjs(selected.start_date), "day")
      ) {
        errors.start_date = "Start date must be before the access until date.";
        errors.access_until =
          "Access until date can't be before the start date.";
      }
      if (Object.keys(errors).length) {
        setDateError(errors);
        return;
      }
    }

    const changed =
      snapshot(productFeatures) !== initialProductFeatures ||
      JSON.stringify(selectedAccess) !== initialSelectedAccess;
    if (!changed) return close();

    setSaving(true);
    try {
      // Edit Product updates the existing per-company-product row, so it
      // needs that row's own id back (access_record_id, from the company-
      // features GET -- see mapCompanyProduct in shared.js) on top of
      // product_id (no company_id), or the backend has nothing to key the
      // update off of. Add Product has no existing row yet, so its own
      // save() (AddProduct.js) omits it and POSTs instead.
      const result = await customAPI(
        `${domain}product/access/company-features/`,
        "PUT",
        {
          id: selected.access_record_id,
          product_id: product.id,
          access_type:
            ACCESS_TYPE_API_VALUES[selected.access_type] ||
            ACCESS_TYPE_API_VALUES[ACCESS_FIELD_DEFAULTS.access_type],
          is_active: isEffectivelyActive(selected),
          ...(selected.access_type !== "Internal" && {
            start_date: selected.start_date || null,
            end_date: selected.access_until || null,
            auto_expire: Boolean(selected.auto_expire),
          }),
          features: productFeatures.map((item) => ({
            feature_id: item.feature_id ?? item.id,
            is_active: Boolean(item.is_active),
          })),
        },
      );
      if (result.ok) {
        notifyAccessChanged();
        showToast("success", "Product and feature configuration saved.");
        onRefresh();
        close();
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={close}
      title={canEdit ? "Edit Product" : "Product Details"}
      subtitle={canEdit ? "Enable or disable features for the selected product." : "Features enabled for the selected product."}
      breadcrumb={["System Configuration", "Product & Feature Configuration"]}
      width={PANEL_DRAWER_WIDTH}
      footer={canEdit &&
        <SaveConfigurationFooter
          onCancel={close}
          onSave={save}
          saving={saving}
        />
      }
    >
      <Mui.Stack
        spacing={3}
        {...(canEdit ? {} : { inert: "" })}
        sx={{ pt: 2.5, px: 3, pb: 2.5, ...(canEdit ? {} : { pointerEvents: "none" }) }}
      >
        <Mui.Box>
          <GlobalFieldLabel helperText="Product being configured.">
            Product
          </GlobalFieldLabel>
          <Mui.Box
            sx={{
              display: "flex",
              alignItems: "center",
              minHeight: 46,
              px: 1.75,
              border: `1px solid ${designColors.slate200}`,
              borderRadius: "8px",
              bgcolor: designColors.slateBg,
              fontSize: 14,
              fontWeight: 600,
              color: designColors.slate700,
            }}
          >
            {product?.name || "--"}
          </Mui.Box>
        </Mui.Box>
        <ProductFeatureStatCards
          total={productFeatures.length}
          basic={productBasicFeatures.length}
          addons={productAddonFeatures.length}
        />
        <AccessValiditySection
          selected={selected}
          dateError={dateError}
          setDateError={setDateError}
          onFieldChange={updateProductField}
        />
        <ProductFeaturesSection
          featureSearch={featureSearch}
          setFeatureSearch={setFeatureSearch}
          basicFeatures={productBasicFeatures}
          addonFeatures={productAddonFeatures}
          onToggleFeature={updateProductFeature}
        />
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
